import clsx from 'clsx';
import { type MutableRefObject, useContext } from 'react';
import {
  ComboBoxStateContext as RACComboBoxStateContext,
  Input as RACInput,
  InputContext as RACInputContext,
  TextContext as RACTextContext,
  useSlottedContext,
} from 'react-aria-components';
import { textStyle } from '../typography/utils.js';

/** @internal */
export function TagFieldInput({
  inputRef,
  isReadOnly,
  isRequired,
  isInvalid,
  placeholder,
  onFocusChange,
  'aria-describedby': ariaDescribedby,
}: {
  inputRef: MutableRefObject<HTMLInputElement | null>;
  isReadOnly: boolean | undefined;
  isRequired: boolean | undefined;
  isInvalid: boolean | undefined;
  placeholder: string | undefined;
  onFocusChange: (isFocused: boolean) => void;
  'aria-describedby': string | undefined;
}) {
  const comboBoxState = useContext(RACComboBoxStateContext);
  const inputContext = useContext(RACInputContext);

  // We use custom IDs for description and error message, so we need to manually attach them to the input
  const { id: descriptionId } = useSlottedContext(RACTextContext, 'description')!;
  const { id: errorMessageId } = useSlottedContext(RACTextContext, 'errorMessage')!;

  return (
    <RACInputContext.Provider
      value={{
        ...inputContext,
        // Change `required` to `aria-required` so that form submission isn't blocked, but required state is still announced by screen readers
        required: false,
        'aria-required': isRequired,
        'aria-invalid': isInvalid || undefined,
      }}
    >
      <RACInput
        ref={inputRef}
        className={clsx('cim-combo-box-input', textStyle({ variant: 'body' }))}
        // Ensure the popover opens when the input is clicked.
        // By default, React Aria only opens the popover when the input is focused.
        onClick={() => !isReadOnly && comboBoxState!.open()}
        placeholder={placeholder}
        onFocus={() => onFocusChange(true)}
        onBlur={() => onFocusChange(false)}
        aria-describedby={[descriptionId, errorMessageId, ariaDescribedby].filter(Boolean).join(' ') || undefined}
      />
    </RACInputContext.Provider>
  );
}
